import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, RotateCcw, MapPin, Truck, Activity } from "lucide-react";

const QUICK_PROMPTS = [
  { label:"Fleet summary",             icon:"📋" },
  { label:"Most toll crossings?",      icon:"🛣"  },
  { label:"Last seen locations",       icon:"📍" },
  { label:"Any alerts?",               icon:"⚠️"  },
  { label:"Show idle vehicles",        icon:"💤" },
];

function buildAnswer(q, vehicles) {
  const ql = q.toLowerCase();
  const active  = vehicles.filter(v => v.status === "Active");
  const idle    = vehicles.filter(v => v.status === "Idle");
  const offline = vehicles.filter(v => v.status === "Offline");

  if (ql.includes("idle")) {
    if (!idle.length) return "No idle vehicles right now. All units are active or offline.";
    return `${idle.length} idle vehicle${idle.length>1?"s":""}:\n${idle.map(v=>`• ${v.nickname} (${v.plateNumber})`).join("\n")}`;
  }

  if (ql.includes("offline")) {
    if (!offline.length) return "No offline vehicles.";
    return `${offline.length} offline:\n${offline.map(v=>`• ${v.nickname} — last at ${v.location}`).join("\n")}`;
  }

  if (ql.includes("most toll") || ql.includes("crossing")) {
    const withHistory = vehicles.filter(v => v.tollHistory?.length > 0);
    if (!withHistory.length) return "No toll history yet. Add vehicles to see FASTag crossings.";
    const sorted = [...withHistory].sort((a,b) => b.tollHistory.length - a.tollHistory.length);
    return `Toll crossings (last 72h):\n${sorted.map((v,i) => `${i+1}. ${v.nickname} — ${v.tollHistory.length} crossing${v.tollHistory.length>1?"s":""}`).join("\n")}`;
  }

  if (ql.includes("last seen") || ql.includes("location")) {
    if (!vehicles.length) return "No vehicles added yet.";
    return `Last known locations:\n${vehicles.map(v=>`• ${v.nickname}\n  ${v.location}`).join("\n")}`;
  }

  if (ql.includes("alert") || ql.includes("warning")) {
    const noData = vehicles.filter(v => v.noRecentData);
    if (!noData.length && !vehicles.length) return "No vehicles added yet.";
    if (!noData.length) return "No alerts. All vehicles have recent FASTag activity.";
    return `${noData.length} vehicle${noData.length>1?"s":""} with no toll activity in 72h:\n${noData.map(v=>`• ${v.nickname} (${v.plateNumber})`).join("\n")}`;
  }

  if (ql.includes("summary") || ql.includes("status") || ql.includes("fleet")) {
    const withData = vehicles.filter(v => v.tollHistory?.length > 0);
    const noData   = vehicles.filter(v => v.noRecentData);
    if (!vehicles.length) return "No vehicles added yet. Use the 'Add Vehicle' button to get started.";
    return `Fleet summary (${vehicles.length} total):\n• Active: ${active.length}\n• Idle: ${idle.length}\n• Offline: ${offline.length}\n• With toll data: ${withData.length}\n• No recent activity: ${noData.length}`;
  }

  if (ql.includes("how many") || ql.includes("count")) {
    return `${vehicles.length} vehicles — ${active.length} active, ${idle.length} idle, ${offline.length} offline.`;
  }

  return `I can answer questions about your fleet.\nTry:\n• Fleet summary\n• Most toll crossings?\n• Last seen locations\n• Any alerts?`;
}

export default function AICopilot({ vehicles = [], isOpen, onToggle }) {
  const [input,  setInput]  = useState("");
  const [msgs,   setMsgs]   = useState([]);
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [msgs, typing]);

  function sendMessage(text) {
    const q = (text || input).trim();
    if (!q) return;
    setInput("");
    setMsgs(prev => [...prev, { role:"user", text:q }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMsgs(prev => [...prev, { role:"ai", text:buildAnswer(q, vehicles) }]);
    }, 400 + Math.random() * 200);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  const noDataCount = vehicles.filter(v => v.noRecentData).length;

  return (
    <>
      {/* FAB */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ opacity:0, scale:.88 }}
            animate={{ opacity:1, scale:1   }}
            exit={{    opacity:0, scale:.88 }}
            transition={{ duration:0.15 }}
            onClick={onToggle}
            title="Fleet Assistant"
            style={{
              position:"fixed", bottom:24, right:24, zIndex:150,
              width:46, height:46, borderRadius:12,
              background:"#2563EB", border:"none",
              cursor:"pointer", color:"#fff",
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:"0 4px 16px rgba(37,99,235,0.4), 0 2px 6px rgba(0,0,0,0.1)",
            }}
          >
            <Activity size={18} />
            {noDataCount > 0 && (
              <div style={{
                position:"absolute", top:-3, right:-3,
                width:14, height:14, borderRadius:"50%",
                background:"#D97706", border:"2px solid #fff",
                fontSize:7, fontWeight:700, color:"#fff",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>{noDataCount}</div>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity:0, y:12, scale:.97 }}
            animate={{ opacity:1, y:0,  scale:1   }}
            exit={{    opacity:0, y:12, scale:.97 }}
            transition={{ duration:0.18, ease:"easeOut" }}
            style={{
              position:"fixed", bottom:24, right:24, zIndex:150,
              width:340, height:480,
              background:"#ffffff",
              border:"1px solid #E5E7EB",
              borderRadius:14, overflow:"hidden",
              boxShadow:"0 20px 56px rgba(0,0,0,0.13), 0 4px 16px rgba(0,0,0,0.06)",
              display:"flex", flexDirection:"column",
            }}
          >
            {/* Header */}
            <div style={{
              padding:"14px 14px 12px",
              borderBottom:"1px solid #F3F4F6",
              display:"flex", alignItems:"center", justifyContent:"space-between",
              flexShrink:0,
              background:"#FAFAFA",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:30, height:30, borderRadius:8,
                  background:"linear-gradient(135deg,#2563EB,#1D4ED8)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow:"0 2px 6px rgba(37,99,235,0.3)",
                }}>
                  <Truck size={13} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>Fleet Assistant</div>
                  <div style={{ fontSize:10, color:"#9CA3AF" }}>
                    {vehicles.length} vehicle{vehicles.length !== 1?"s":""} · FASTag data
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {msgs.length > 0 && (
                  <button
                    onClick={() => setMsgs([])}
                    title="Clear chat"
                    style={{
                      background:"none", border:"1px solid #E5E7EB", borderRadius:6,
                      width:26, height:26, cursor:"pointer", color:"#9CA3AF",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}
                    onMouseOver={e => { e.currentTarget.style.background="#F3F4F6"; e.currentTarget.style.color="#374151"; }}
                    onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.color="#9CA3AF"; }}
                  ><RotateCcw size={10} /></button>
                )}
                <button
                  onClick={onToggle}
                  style={{
                    background:"none", border:"1px solid #E5E7EB", borderRadius:6,
                    width:26, height:26, cursor:"pointer", color:"#9CA3AF",
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}
                  onMouseOver={e => { e.currentTarget.style.background="#F3F4F6"; e.currentTarget.style.color="#374151"; }}
                  onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.color="#9CA3AF"; }}
                ><X size={13} /></button>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:"auto", padding:"12px 12px 6px" }}>
              {msgs.length === 0 && !typing && (
                <div>
                  {/* Alert banner if vehicles have no recent data */}
                  {noDataCount > 0 && (
                    <div style={{
                      background:"#FFFBEB", border:"1px solid #FDE68A",
                      borderRadius:8, padding:"8px 12px", marginBottom:12,
                      display:"flex", alignItems:"center", gap:7,
                    }}>
                      <span style={{ fontSize:13 }}>⚠️</span>
                      <span style={{ fontSize:11, color:"#92400E", fontWeight:500 }}>
                        {noDataCount} vehicle{noDataCount>1?"s":""} with no toll activity in 72h
                      </span>
                    </div>
                  )}

                  <div style={{ textAlign:"center", padding:"8px 0 16px" }}>
                    <div style={{
                      width:44, height:44, borderRadius:12,
                      background:"linear-gradient(135deg,#2563EB,#1D4ED8)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      margin:"0 auto 12px", boxShadow:"0 3px 10px rgba(37,99,235,0.3)",
                    }}>
                      <MapPin size={20} color="#fff" />
                    </div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:4 }}>Fleet Assistant</div>
                    <div style={{ fontSize:11, color:"#9CA3AF" }}>Ask anything about your vehicles</div>
                  </div>

                  <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    {QUICK_PROMPTS.map(p => (
                      <button
                        key={p.label}
                        onClick={() => sendMessage(p.label)}
                        style={{
                          background:"#F9FAFB", border:"1px solid #F3F4F6",
                          borderRadius:8, padding:"8px 11px", cursor:"pointer",
                          display:"flex", alignItems:"center", gap:8,
                          textAlign:"left", color:"#374151", fontSize:12,
                          transition:"all 0.1s",
                        }}
                        onMouseOver={e => { e.currentTarget.style.background="#EFF6FF"; e.currentTarget.style.borderColor="#BFDBFE"; e.currentTarget.style.color="#2563EB"; }}
                        onMouseOut={e  => { e.currentTarget.style.background="#F9FAFB"; e.currentTarget.style.borderColor="#F3F4F6"; e.currentTarget.style.color="#374151"; }}
                      >
                        <span style={{ fontSize:12, flexShrink:0, lineHeight:1 }}>{p.icon}</span>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {msgs.map((m, i) => (
                <div
                  key={i}
                  style={{
                    display:"flex",
                    justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                    marginBottom:8,
                  }}
                >
                  <div style={{
                    maxWidth:"82%",
                    background: m.role === "user" ? "#2563EB" : "#F9FAFB",
                    border: m.role === "user" ? "none" : "1px solid #F3F4F6",
                    borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "4px 12px 12px 12px",
                    padding:"9px 12px",
                    fontSize:12, lineHeight:1.65,
                    color: m.role === "user" ? "#fff" : "#374151",
                    whiteSpace:"pre-wrap",
                  }}>
                    {m.text}
                  </div>
                </div>
              ))}

              {typing && (
                <div style={{ display:"flex", marginBottom:8 }}>
                  <div style={{
                    background:"#F9FAFB", border:"1px solid #F3F4F6",
                    borderRadius:"4px 12px 12px 12px", padding:"10px 14px",
                    display:"flex", gap:4, alignItems:"center",
                  }}>
                    {[0,1,2].map(i => (
                      <motion.div
                        key={i}
                        animate={{ opacity:[0.3,1,0.3], y:[0,-2,0] }}
                        transition={{ duration:0.6, delay:i*0.12, repeat:Infinity }}
                        style={{ width:4, height:4, borderRadius:"50%", background:"#9CA3AF" }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={{ padding:"8px 12px 14px", borderTop:"1px solid #F3F4F6", flexShrink:0 }}>
              <div style={{
                display:"flex", gap:7, alignItems:"center",
                background:"#F9FAFB", border:"1px solid #E5E7EB",
                borderRadius:9, padding:"0 10px",
                transition:"border-color 0.15s, box-shadow 0.15s",
              }}
                onFocusCapture={e => { e.currentTarget.style.borderColor="#2563EB"; e.currentTarget.style.boxShadow="0 0 0 3px rgba(37,99,235,0.08)"; }}
                onBlurCapture={e  => { e.currentTarget.style.borderColor="#E5E7EB"; e.currentTarget.style.boxShadow="none"; }}
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Ask about your fleet…"
                  style={{
                    flex:1, background:"none", border:"none", outline:"none",
                    fontSize:12, color:"#111827", padding:"10px 0",
                  }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || typing}
                  style={{
                    background: input.trim() && !typing ? "#2563EB" : "#E5E7EB",
                    border:"none", borderRadius:6, width:24, height:24,
                    cursor: input.trim() && !typing ? "pointer" : "default",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color: input.trim() && !typing ? "#fff" : "#9CA3AF",
                    flexShrink:0, transition:"all 0.15s",
                  }}
                ><Send size={11} /></button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
